// Organization role types - ordered by permission level (lowest to highest)
export type OrganizationRole = 'viewer' | 'member' | 'admin' | 'owner';

// Role hierarchy for permission checks
export const ROLE_HIERARCHY: Record<OrganizationRole, number> = {
  viewer: 0,
  member: 1,
  admin: 2,
  owner: 3,
};

export type MfaEnforcementPolicy = 'disabled' | 'optional' | 'required';

export interface MfaPolicySettings {
  policy: MfaEnforcementPolicy;
  required_for_roles: OrganizationRole[]; // empty array = all roles when policy=required
  grace_period_days: number;
  policy_enacted_at: string | null;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  settings: Record<string, unknown>;
  audit_retention_days?: number;
  mfa_policy?: MfaPolicySettings;
  role: OrganizationRole; // current user's role
  created_at: string;
  updated_at: string;
}

export interface OrganizationListItem {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  role: OrganizationRole;
}

export type ProjectRoleScope = 'all_projects' | 'project';

export interface MemberProjectRole {
  id: string;
  project_id: string | null;
  project_slug: string | null;
  project_name: string | null;
  scope: ProjectRoleScope;
  role: OrganizationRole;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  role: OrganizationRole;
  joined_at: string;
  mfa_enabled?: boolean;
  mfa_required?: boolean;
  mfa_setup_overdue?: boolean;
  mfa_deadline?: string | null;
  project_roles: MemberProjectRole[];
}

export interface Invitation {
  id: string;
  email: string;
  role: OrganizationRole;
  status: 'pending' | 'accepted' | 'expired';
  invited_by: {
    name: string;
    email: string;
  };
  expires_at: string;
  created_at: string;
}

export interface InvitationPreview {
  organization_name: string;
  role: OrganizationRole;
  inviter_name: string;
  expires_at: string;
  expired: boolean;
}

// API Request types
export interface UpdateMfaPolicyRequest {
  policy?: MfaEnforcementPolicy;
  grace_period_days?: number;
  required_for_roles?: OrganizationRole[];
}

export interface MfaPolicyResponse {
  success: boolean;
  data?: { mfa_policy: MfaPolicySettings };
  error?: string;
}

export interface CreateOrganizationRequest {
  name: string;
  slug: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  logo_url?: string;
  settings?: Record<string, unknown>;
  audit_retention_days?: number;
}

export interface InviteMemberRequest {
  email: string;
  role: OrganizationRole;
}

export interface UpdateMemberRoleRequest {
  role: OrganizationRole;
}

export interface SetProjectRoleRequest {
  role: OrganizationRole;
}

export interface ProjectRoleListResponse {
  success: boolean;
  data?: MemberProjectRole[];
  error?: string;
}

export interface ProjectRoleResponse {
  success: boolean;
  data?: MemberProjectRole;
  error?: string;
  details?: string[];
}

// API Response types
export interface OrganizationListResponse {
  success: boolean;
  data?: OrganizationListItem[];
  error?: string;
}

export interface OrganizationResponse {
  success: boolean;
  data?: Organization;
  error?: string;
  details?: string[];
}

export interface MemberListResponse {
  success: boolean;
  data?: Member[];
  error?: string;
}

export interface MemberResponse {
  success: boolean;
  data?: Member;
  error?: string;
}

export interface InvitationResponse {
  success: boolean;
  data?: Invitation;
  error?: string;
  details?: string[];
}

export interface InvitationListResponse {
  success: boolean;
  data?: Invitation[];
  error?: string;
}

export interface InvitationPreviewResponse {
  success: boolean;
  data?: InvitationPreview;
  error?: string;
}

export interface AcceptInvitationResponse {
  success: boolean;
  message?: string;
  data?: {
    organization_id: string;
    organization_name: string;
    role: OrganizationRole;
  };
  error?: string;
}

export interface DeleteResponse {
  success: boolean;
  message?: string;
  error?: string;
}
